import React, { ReactNode } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { usePreview, usePreviewState } from 'react-dnd-preview'
import { TouchBackend } from 'react-dnd-touch-backend'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import config from '@config/index'
import { AuthProvider } from '@modules/auth/hooks/useAuthContext'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import ErrorBoundary from '@shared/ErrorBoundary'
import { DRAG_AUTO_SCROLL_PX_AMOUNT, TOP_SCROLL_THRESHOLD } from '@shared/constants'
import { LocationHistoryProvider } from '@shared/hooks/useLocationHistory'
import { NotificationProvider } from '@shared/hooks/useNotification'
import { isTouchDevice } from '@shared/utils'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { colors } from '@theme/variables'

import App from './app'
import './app/i18n'
import mainTheme from './app/theme/index'
import './app/theme/overrides.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 3,
      gcTime: 1000 * 60 * 30,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  },
})

const DragEmulatorPreview = () => {
  const preview: usePreviewState<{ renderer: () => ReactNode }, Element> = usePreview()

  if (!preview.display || !isTouchDevice) {
    return null
  }

  const { itemType, item, style } = preview

  // this emulates auto scroll for touch devices, by checking if preview is drawn too close to the top of the window
  const regex = /,(.*?)\px/
  const matched = (regex.exec(style.transform as string) as Array<string>)[1]

  if (Number(matched) < TOP_SCROLL_THRESHOLD) {
    window.scrollTo(0, window.scrollY - DRAG_AUTO_SCROLL_PX_AMOUNT)
  }

  // Tablet doesnt have native drag 'ghost' element, so its and emulation of one
  if (itemType === 'profile') {
    const resultingStyle = {
      ...style,
      background: colors.white,
      maxWidth: '465px',
      display: 'flex',
      zIndex: 1000,
      alignItems: 'center',
      boxShadow: '4px 4px 4px 0px #0000001A',
      color: colors.bodyText,
    }

    return <div style={resultingStyle}>{item.renderer()}</div>
  }
  return null
}

const Root = () => {
  return (
    <React.StrictMode>
      <>
        <CssBaseline />
        <ThemeProvider theme={mainTheme}>
          <BrowserRouter>
            <DndProvider backend={isTouchDevice ? TouchBackend : HTML5Backend} options={{ enableTouchEvents: isTouchDevice }}>
              <QueryClientProvider client={queryClient}>
                <AuthProvider>
                  <LocationHistoryProvider>
                    <QueryParamProvider adapter={ReactRouter6Adapter}>
                      <NotificationProvider>
                        <ErrorBoundary>
                          <App />
                        </ErrorBoundary>
                      </NotificationProvider>
                    </QueryParamProvider>
                  </LocationHistoryProvider>
                </AuthProvider>
                {config.enableDevTools && <ReactQueryDevtools initialIsOpen={false} />}
              </QueryClientProvider>
              <DragEmulatorPreview />
            </DndProvider>
          </BrowserRouter>
        </ThemeProvider>
      </>
    </React.StrictMode>
  )
}

const domRoot = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

domRoot.render(<Root />)
